import React, { useState, useEffect, useRef } from "react";
import { inject, observer } from "mobx-react";
import BroadcastStore from "common/broadcast/_stores/broadcast/BroadcastStore";
import TailwindButton from "library/components/_tailwind/button";
import TailwindInput from "library/components/_tailwind/input";
import { TailwindInputType } from "library/components/_tailwind/input-base";
import { BROADCAST_END_TIPPING_SHOW_EARLY_SHOW_STOP_WARNING_TIME_IN_MINUTES } from "common/broadcast/_stores/broadcast/consts";
import ProfileStore from "common/my-page/stores/profile/ProfileStore";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindSwitch from "library/components/_tailwind/switch";
import { history } from "library/core/utility";
import NodeChatStore from "common/broadcast/_stores/nodechat/NodeChatStore";
import { LovenseToyStatus } from "common/broadcast/_stores/nodechat/types";

type Props = {
  broadcastStore?: BroadcastStore;
  profileStore?: ProfileStore;
  nodeChatStore?: NodeChatStore;
};

const QAWidgetMisc: React.ComponentType<Props> = ({
  broadcastStore,
  profileStore,
  nodeChatStore,
}) => {
  const {
    endTippingShowEarlyShowStopWarningTimeInSeconds,
    setEndTippingShowEarlyShowStopWarningTimeInSeconds,
  } = broadcastStore!;
  const { modelProfile, toggleOptInToMemberVerification } = profileStore!;
  const { lovenseToyStatusChange, incrementSessionEarnings } = nodeChatStore!;

  const [simulateTokens, setSimulateTokens] = useState<string>("");
  const [isMockLovenseEnabled, setIsMockLovenseEnabled] = useState<boolean>(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Mock toy data
  const mockToys: LovenseToyStatus[] = [
    {
      id: "mock-1",
      name: "Lush 3",
      type: "vibrator",
      status: "on",
      version: "1.0",
      battery: 80,
    },
    {
      id: "mock-2",
      name: "Edge 2",
      type: "vibrator",
      status: "on",
      version: "1.0",
      battery: 65,
    },
    {
      id: "mock-3",
      name: "Nora",
      type: "vibrator",
      status: "off",
      version: "1.0",
      battery: 45,
    },
    {
      id: "mock-4",
      name: "Hush 2",
      type: "vibrator",
      status: "on",
      version: "1.0",
      battery: 90,
    },
    {
      id: "mock-5",
      name: "Domi 2",
      type: "vibrator",
      status: "on",
      version: "1.0",
      battery: 55,
    },
    {
      id: "mock-6",
      name: "Osci 2",
      type: "vibrator",
      status: "off",
      version: "1.0",
      battery: 30,
    },
    {
      id: "mock-7",
      name: "Ferri",
      type: "vibrator",
      status: "on",
      version: "1.0",
      battery: 75,
    },
    {
      id: "mock-8",
      name: "Diamo",
      type: "vibrator",
      status: "off",
      version: "1.0",
      battery: 20,
    },
  ];

  // Toggle mock Lovense toys
  const toggleMockLovense = () => {
    if (!isMockLovenseEnabled) {
      // Enable mock - set initial toys
      lovenseToyStatusChange(mockToys);

      // Start random status changes
      intervalRef.current = setInterval(() => {
        const updatedToys = mockToys.map(toy => ({
          ...toy,
          status: Math.random() > 0.5 ? "on" : "off",
          battery: Math.max(5, Math.min(100, toy.battery! - Math.floor(Math.random() * 10))),
        }));
        lovenseToyStatusChange(updatedToys);
      }, 3000); // Change status every 3 seconds
    } else {
      // Disable mock - clear toys and interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      lovenseToyStatusChange([]);
    }

    setIsMockLovenseEnabled(!isMockLovenseEnabled);
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleSimulateEarnings = () => {
    const tokens = parseInt(simulateTokens, 10);
    if (!isNaN(tokens) && tokens > 0) {
      incrementSessionEarnings("tokens", tokens);
      setSimulateTokens("");
    }
  };

  return (
    <TailwindFlex flexDirection='flex-col' gap='gap-4'>
      <TailwindFlex flexDirection='flex-col' gap='gap-1'>
        <div>Simulate Token Earnings</div>
        <TailwindFlex gap='gap-2' alignItems='items-center'>
          <TailwindInput
            value={simulateTokens}
            onChange={(e) => setSimulateTokens(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSimulateEarnings();
            }}
            placeholder='Token amount'
            type={TailwindInputType.number}
          />
          <TailwindButton
            backgroundColor='primary'
            onClick={handleSimulateEarnings}
          >
            Add Tokens
          </TailwindButton>
        </TailwindFlex>
      </TailwindFlex>

      <TailwindFlex gap='gap-2'>
        <div>Opt in account to Member Verification</div>
        <TailwindSwitch
          onLabel={"On"}
          offLabel={"Off"}
          size={"xs"}
          onChange={async isChecked => {
            await toggleOptInToMemberVerification();

            if (isChecked) {
              history.push("/broadcast-member-verification");
            }
          }}
          isChecked={!!modelProfile.flags?.opted_in_for_member_verification}
          offBackgroundColor={"bg-toggle-disabled"}
          offLabelProps={{
            textColor: "text-white",
          }}
          onBackgroundColor={"bg-earnings-bg-color"}
          onLabelProps={{
            textColor: "text-main-color",
          }}
          pillBackgroundColor={
            !!modelProfile.flags?.opted_in_for_member_verification
              ? "bg-green-500"
              : "bg-gray-200"
          }
        />
      </TailwindFlex>

      <TailwindButton
        backgroundColor={isMockLovenseEnabled ? "secondary" : "primary"}
        onClick={toggleMockLovense}>
        {isMockLovenseEnabled ? (
          <React.Fragment>
            <b>Disable</b> Mock Lovense Toys (8 toys with random status changes every 3s)
          </React.Fragment>
        ) : (
          <React.Fragment>
            <b>Enable</b> Mock Lovense Toys (8 toys with random status changes every 3s)
          </React.Fragment>
        )}
      </TailwindButton>

      <TailwindButton
        backgroundColor={"primary"}
        onClick={() =>
          endTippingShowEarlyShowStopWarningTimeInSeconds === 10
            ? setEndTippingShowEarlyShowStopWarningTimeInSeconds(
                BROADCAST_END_TIPPING_SHOW_EARLY_SHOW_STOP_WARNING_TIME_IN_MINUTES *
                  60
              )
            : setEndTippingShowEarlyShowStopWarningTimeInSeconds(10)
        }>
        {endTippingShowEarlyShowStopWarningTimeInSeconds === 10 ? (
          <React.Fragment>
            Set tipping show early show stop (curtain down) warning time to the
            default of 5 minutes
          </React.Fragment>
        ) : (
          <React.Fragment>
            Set tipping show early show stop (curtain down) warning time to{" "}
            <b>10 seconds</b> (normally 5 minutes)
          </React.Fragment>
        )}
      </TailwindButton>
    </TailwindFlex>
  );
};

export default inject("broadcastStore", "profileStore", "nodeChatStore")(observer(QAWidgetMisc));
